'use client';

import React, { FC } from 'react';
import {
  TypeLinkBannerFields,
  TypeLinkBannerPageFields,
} from '@/core/generated-types';
import { Entry } from 'contentful';
import { Link } from '@/navigation';
import { useParams } from 'next/navigation';
import { ChevronIcon } from '@/components/ui';
import { getRouteForPage } from '@/core/contentfulConfig';
import classNames from 'classnames';

function getLinkMeta(
  pathname: string,
  links: Entry<TypeLinkBannerPageFields>[] = [],
) {
  const activeIndex = Math.max(
    0,
    links.findIndex((link) => {
      const slug = link.fields.linkContent?.fields?.slug;
      return pathname.endsWith(slug);
    }),
  );

  const prevIndex = activeIndex === 0 ? -1 : activeIndex - 1;
  const nextIndex = activeIndex === links.length - 1 ? -1 : activeIndex + 1;

  return {
    prevLinkContent: prevIndex > -1 && links[prevIndex].fields.linkContent,
    activeLink: links[activeIndex],
    nextLinkContent: nextIndex > -1 && links[nextIndex].fields.linkContent,
  };
}

const LinkBanner: FC<TypeLinkBannerFields> = ({ links }) => {
  const urlParams = useParams();
  const slug = urlParams?.slug as string;

  const { activeLink, prevLinkContent, nextLinkContent } = getLinkMeta(
    slug || '',
    links,
  );

  const activeLinkBg = activeLink?.fields?.backgroundColor?.fields?.color;

  const bgClass = activeLinkBg ? `bg-${activeLinkBg}` : 'bg-indigo-pastel';

  return (
    <div
      className={`${bgClass} absolute left-0 top-0 flex w-full items-center justify-center md:hidden`}
    >
      <div className="flex w-full max-w-[325px] items-center">
        <Link
          data-testid="link-banner-prev"
          className={classNames({ invisible: !prevLinkContent })}
          href={
            prevLinkContent
              ? getRouteForPage(
                  prevLinkContent.sys.contentType.sys.id,
                  prevLinkContent.fields.contentGroup as string,
                  prevLinkContent.fields.slug,
                )
              : ''
          }
        >
          <ChevronIcon direction="left" width={16} height={16} />
        </Link>
        <div className="flex-auto py-3 text-center">
          <div className="text-lg font-bold" data-testid="link-banner-title">
            {activeLink.fields.title}
          </div>
          <div className="" data-testid="link-banner-subtitle">
            {activeLink.fields.subtitle}
          </div>
        </div>
        {nextLinkContent && (
          <Link
            data-testid="link-banner-next"
            className={classNames({ invisible: !nextLinkContent })}
            href={
              nextLinkContent
                ? getRouteForPage(
                    nextLinkContent.sys.contentType.sys.id,
                    nextLinkContent.fields.contentGroup as string,
                    nextLinkContent.fields.slug,
                  )
                : ''
            }
          >
            <ChevronIcon direction="right" width={16} height={16} />
          </Link>
        )}
      </div>
    </div>
  );
};

export default React.memo(LinkBanner);
